import Urn from '@adc/structures/urn'
import React, { useState, useCallback, useEffect } from 'react'
// @ts-ignore no types
import pluralize from 'pluralize'
import {
  Modal,
  Button,
  Text,
  InputField,
  SelectField,
  useApplicationNotifications,
  CardContainer,
  coreConstants,
  NOTIFICATION_ROLES,
} from '@adc/ui-morpheus'
import logger from '@adc/logger'
import classnames from 'classnames/bind'
import { fetchUsersByEmail } from '../../../providers/user'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const constants = coreConstants

interface BulkAssignModalProps {
  isOpen: boolean
  onClose: () => void
  onAssign: (assigneeUrn: string, size: number) => Promise<void>
  totalFilteredCount: number
}

const BulkAssignModal: React.FC<BulkAssignModalProps> = ({
  isOpen,
  onClose,
  onAssign,
  totalFilteredCount,
}) => {
  const notifications = useApplicationNotifications() // eslint-disable-line
  const [assigneeUrn, setAssigneeUrn] = useState<string>('')
  const [rowLimit, setRowLimit] = useState<number>(50)
  const [isAssigning, setIsAssigning] = useState<boolean>(false)
  const [assigneeOptions, setAssigneeOptions] = useState<
    Array<{ text: string; value: string }>
  >([])
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')

  // Fetch users based on search term
  const fetchUsers = useCallback(
    async (search: string) => {
      if (!search || search.length < 2) {
        setAssigneeOptions([])
        return
      }

      setIsLoadingUsers(true)
      try {
        const users = await fetchUsersByEmail(search, 10)
        const options = (
          users as Array<{ email: string; party_id: string }>
        ).map((user: { party_id: string; email: string }) => ({
          value: new Urn('party', user.party_id).toString(),
          text: user.email,
        }))
        setAssigneeOptions(options)
      } catch (error) {
        logger.error('Failed to fetch users:', error)
        // eslint-disable-next-line
        notifications.toast.open({
          role: NOTIFICATION_ROLES.ERROR,
          title: 'Failed to fetch users',
        })
        setAssigneeOptions([])
      } finally {
        setIsLoadingUsers(false)
      }
    },
    [notifications]
  )

  // Debounced search effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm) {
        fetchUsers(searchTerm) // eslint-disable-line
      }
    }, 300)

    return () => clearTimeout(delayDebounceFn)
  }, [searchTerm, fetchUsers])

  const handleAssign = useCallback(async () => {
    if (!assigneeUrn) {
      // eslint-disable-next-line
      notifications.toast.open({
        title: 'Please select an assignee',
        role: NOTIFICATION_ROLES.ERROR,
      })
      return
    }

    setIsAssigning(true)
    try {
      await onAssign(assigneeUrn, rowLimit)
      // eslint-disable-next-line
      notifications.toast.open({
        title: `Successfully assigned ${Math.min(
          rowLimit,
          totalFilteredCount
        )} evaluation groups`,
        role: NOTIFICATION_ROLES.SUCCESS,
      })
      onClose()
    } catch {
      // eslint-disable-next-line
      notifications.toast.open({
        title: 'Failed to assign evaluation groups',
        role: NOTIFICATION_ROLES.ERROR,
      })
    } finally {
      setIsAssigning(false)
    }
  }, [
    assigneeUrn,
    rowLimit,
    onAssign,
    onClose,
    notifications,
    totalFilteredCount,
  ])

  const handleRowLimitChange = useCallback((value: string) => {
    const numValue = parseInt(value, 10)
    if (!isNaN(numValue)) {
      // Enforce max limit of 200
      setRowLimit(Math.min(Math.max(1, numValue), 200))
    }
  }, [])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAssigneeUrn('')
      setRowLimit(50)
      setSearchTerm('')
      setAssigneeOptions([])
    }
  }, [isOpen])

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size='tiny'
      elmId='bulk_assign_modal'
      className={cx('bulk-assign-modal')}
    >
      <Modal.Header>
        <Text size='large' weight='heavy'>
          Assign for Review
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div className={cx('modal-content', 'ui', 'form')}>
          <div className={cx('form-field')}>
            <SelectField
              label='Assign To:'
              value={assigneeUrn}
              onChange={(_e: unknown, data: { value: string }) => {
                return setAssigneeUrn(data.value)
              }}
              onSearchChange={(_e: unknown, data: { searchQuery: string }) =>
                setSearchTerm(data.searchQuery)
              }
              options={assigneeOptions}
              placeholder='Search...'
              elmId='assignee_dropdown'
              search
              loading={isLoadingUsers}
              noResultsMessage={
                searchTerm.length < 2
                  ? 'Type at least 2 characters to search'
                  : 'No users found'
              }
              fluid
              autoComplete='off'
              aria-autocomplete='off'
            />
          </div>

          <div className={cx('form-field')}>
            <InputField
              label='Limit to the First:'
              value={rowLimit.toString()}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleRowLimitChange(e.target.value)
              }
              type='number'
              min={1}
              max={200}
              elmId='row_limit_input'
              fluid
              helpContent={`Maximum 200 rows. ${
                totalFilteredCount > 0
                  ? `Currently ${Math.min(
                      rowLimit,
                      totalFilteredCount
                    )} of the ${totalFilteredCount.toLocaleString()} filtered rows will be assigned.`
                  : ''
              }`}
            />
          </div>

          {totalFilteredCount > 0 && rowLimit > totalFilteredCount && (
            <CardContainer
              padding='small'
              backgroundColor={constants.ColorNames.OffWhite}
            >
              <Text size='small' color='gray'>
                {/* eslint-disable-next-line */}
                Note: Only {pluralize('row', totalFilteredCount, true)}{' '}
                {totalFilteredCount === 1 ? 'matches' : 'match'} the current
                filter. All filtered rows will be assigned.
              </Text>
            </CardContainer>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button
          onClick={onClose}
          disabled={isAssigning}
          elmId='bulk_assign_cancel_button'
        >
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          role='primary'
          disabled={isAssigning || !assigneeUrn}
          loading={isAssigning}
          elmId='bulk_assign_submit_button'
        >
          Assign
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default BulkAssignModal
